import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { LanguageService } from '@igo2/core/language';
import { ZzzConfigService } from 'src/app/zzz/core/config/config.service';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { IgoLanguageModule } from '@igo2/core/language';
import { IgoMap, roundCoordTo, roundCoordToString } from '@igo2/geo';
import { Coordinate } from 'ol/coordinate';
import * as olProj from 'ol/proj';
import { MapState } from '@igo2/integration';
import { MatIconModule } from '@angular/material/icon';
import { ExclusiveMapEventsService } from 'src/app/shared/services/exclusive-map-events.service';
import { MatTooltipModule } from '@angular/material/tooltip';

interface LegendOption {
  id: string;
  title: string;
}

type ReportTitles = [string, string];

@Component({
    selector: 'app-popup-legend-selector',
    templateUrl: './popup-legend-selector.component.html',
    styleUrls: ['./popup-legend-selector.component.scss'],
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatInputModule,
        MatSelectModule,
        MatFormFieldModule,
        FormsModule,
        IgoLanguageModule
    ]
})
export class PopupLegendSelectorComponent {
  //UI strings
  title: string;
  reportTitleLabel: string;
  reportTitlePlaceHolder:string;
  legendSelectorLabel: string;
  legendSelectorPlaceholder: string;
  buttonOk: string;
  buttonCancel: string;
  // Legend selection
  legendOptions: LegendOption[] = [];
  selectedLegendId: string = '';
  legendImageUrl: string = '';
  // Report titles
  reportTitleLine1: string;
  _reportTitles:ReportTitles;

  // Map + pick mode
  igoMap: IgoMap;
  mapCenterPoint: {text?: string, coordinates?: Coordinate} = {};
  private restoreExclusiveMapEvents?: () => void;
  private readonly coordRoundedDecimals = 6;
  private readonly projection = 'EPSG:4326';

  // View state
  showDialog:boolean =  true;
  suppressFocus = false;

constructor(
    private languageService: LanguageService,
    private http: HttpClient,
    private configService: ZzzConfigService,
    public dialogRef: MatDialogRef<PopupLegendSelectorComponent>,
    private mapState: MapState,
    private exclusiveMapEventsService: ExclusiveMapEventsService,
    @Inject(MAT_DIALOG_DATA) public data: {reportTiles:ReportTitles}
  ) {
    this.title = this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.legendPopupTitle');
    this.reportTitleLabel = this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.reportTitleLabel');
    this.reportTitlePlaceHolder= this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.reportTitlePlaceHolder');
    this.legendSelectorLabel = this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.legendSelectorLabel');
    this.legendSelectorPlaceholder = this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.legendSelectorPlaceholder');
    this.buttonOk = this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.buttonOk');
    this.buttonCancel = this.languageService.translate.instant('sigt.integration.rechercheGdf.form.serviceGdf.buttonCancel');

    // Initialize with provided titles
    this._reportTitles = data.reportTiles;
    this.reportTitleLine1 = this._reportTitles[0];
  }

  ngOnInit(): void {
    this.fetchLegendOptions();
    this.igoMap = this.mapState.map;
    this.showDialog = true;
  }

  ngOnDestroy() {
    // Safety: if the dialog is destroyed mid-exclusivity, restore OL dispatch and cursor
    this.deactivatePickMode();
  }

  get urlBaseApi(): string {
    return this.configService.get().authApiEndPoint + "/sigtapi";
  }

  fetchLegendOptions(): void {
    const uri = `${this.urlBaseApi}/legendes`;
    this.http.get<LegendOption[]>(uri).subscribe({
      next: (data) => {
        this.legendOptions = data;
      },
      error: (err) => {
        console.error('Failed to fetch legend options', err);
      }
    });
  }

  onLegendChange(id: string): void {
    this.selectedLegendId = id;
    const uri = `${this.urlBaseApi}/legende`;
    if (id) {
      this.legendImageUrl = `${uri}/${id}`;
    } else {
      this.legendImageUrl = '';
    }
  }

  onCancel(): void {
    this.dialogRef.close({
      confirmed: false,
      selectedId: undefined,
      reportTitles: undefined,
      mapCenterPoint: undefined
    });
  }

  onConfirm(): void {
    const reportTitles: ReportTitles = [this.sanitizeInput(this.reportTitleLine1 ?? ''), this._reportTitles[1]];
    this.dialogRef.close({
      confirmed: true,
      selectedId: this.selectedLegendId,
      reportTitles,
      mapCenterPoint: this.mapCenterPoint.coordinates
    });
  }

  /**
   * Protects against code injection
   */
  private sanitizeInput(input: string): string {
    // Remove raw HTML tags
    const stripped = input.replace(/<\/?[^>]+(>|$)/g, '');

    const tempDiv = document.createElement('div');
    tempDiv.textContent = stripped; // Escapes HTML
    return tempDiv.innerHTML;
  }
  /**
   * View handlers
   */
  onInputFocus() {
    if (this.suppressFocus) {
      return;
    }
    // Hide dialog while picking on the map
    this.showDialog = false;
    this.hideDialogBackdrop();
    // Enter exclusive mode:
    this.activatePickMode();
  }

  blockTyping(event: KeyboardEvent): void {
    const allowedKeys = ['Tab', 'ArrowLeft', 'ArrowRight'];
    if (!allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  clearInput(event?:MouseEvent):void {
    event?.preventDefault(); // Prevent input from being focused
    this.mapCenterPoint = {};
    // Move focus back to dialog container
    setTimeout(() => {
        const dialogContainer = document.querySelector('.mat-mdc-dialog-container') as HTMLElement;
        dialogContainer?.focus();
    }, 50);
    //This is needed to avoid going back into map click mode
    this.suppressFocus = true;
    setTimeout(() => {
      this.suppressFocus = false;
    }, 500); // Adjust timing as needed
  }  

  /**
   * Listens for a single click event on the map and uses the coordinates of the click
   *
   */
  
 private setInteractionsActive(active:boolean) {
      if (this.igoMap) {
        this.igoMap.ol.getInteractions().forEach(i => i.setActive(active));
      }
    }

    /**
   * Uses the given coordinates to update the map center.
   *
   * @param {Coordinate} coordinates - The coordinates to transform and round.
   */
  private useCoordinatesAsCenter(coordinates: Coordinate): void {
    const projectedCoordinates: Coordinate = olProj.transform(
      coordinates,
      this.igoMap.projection,
      this.projection
    );
    const roundedCoordinates: Coordinate = roundCoordTo(
      projectedCoordinates,
      this.coordRoundedDecimals
    );

    this.mapCenterPoint.text = roundCoordToString(
      projectedCoordinates,
      this.coordRoundedDecimals
    ).join(', ');
    this.mapCenterPoint.coordinates = coordinates;

  }

  //SIGT-1542
  activatePickMode() {
    if (!this.igoMap) return;
    this.setInteractionsActive(false);
    this.igoMap.ol.getViewport().style.cursor = 'crosshair';

    this.restoreExclusiveMapEvents = this.exclusiveMapEventsService.enable(this.igoMap.ol, {
      block: ['singleclick', 'pointermove'],
      handlers: {
        singleclick: (evt) => {
          this.useCoordinatesAsCenter(evt.coordinate);
          // Restore UI and interactions
          this.deactivatePickMode();
          this.showDialog = true;
          this.restoreDialogBackdrop();
        }
      }
    });
  }
  deactivatePickMode() {
    try {
      this.restoreExclusiveMapEvents?.();
    } finally {
      this.restoreExclusiveMapEvents = undefined;
      this.setInteractionsActive(true);
      if (this.igoMap) {
        this.igoMap.ol.getViewport().style.cursor = '';
      }
    }
  }

  // --- Backdrop helpers (keep hacks contained) ---
  private hideDialogBackdrop(): void {
    const backdrop = document.querySelector('.cdk-overlay-backdrop') as HTMLElement | null;
    if (backdrop) backdrop.style.display = 'none';
  }

  private restoreDialogBackdrop(): void {
    const backdrop = document.querySelector('.cdk-overlay-backdrop') as HTMLElement | null;
    if (backdrop) backdrop.style.display = '';
  }  
}

